import { Injectable, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { LocalStorage } from "@dffedb/util";

import {
    FEATURE_HIGHLIGHT_PARENT,
    FEATURE_HIGHLIGHT_STORAGE_KEY,
    FeatureHighlight,
    FeatureHighlightKey,
    FeatureHighlightParent
} from "./feature-highlight.model";
import { FeatureHighlightSource } from "./feature-highlight.source";

@Injectable({ providedIn: "root" })
export class FeatureHighlightService {
    private readonly localStorage = inject(LocalStorage);
    private readonly highlights = signal<readonly FeatureHighlight[]>([]);
    private readonly dismissed = signal<ReadonlySet<string>>(this.readDismissed());

    /** Første ikke-dismissede highlight pr. feature-nøgle. */
    private readonly aktive = computed(() => {
        const dismissed = this.dismissed();
        const result = new Map<FeatureHighlightKey, FeatureHighlight>();
        for (const highlight of this.highlights()) {
            if (!result.has(highlight.noegle) && !dismissed.has(dismissKey(highlight))) {
                result.set(highlight.noegle, highlight);
            }
        }
        return result;
    });

    constructor() {
        inject(FeatureHighlightSource)
            .hent()
            .pipe(takeUntilDestroyed())
            .subscribe((highlights) => {
                const now = Date.now();
                const gyldige = highlights.filter((highlight) => !highlight.udloeb || highlight.udloeb.getTime() > now);
                this.highlights.set(gyldige);
                this.ryddOpDismissed(gyldige);
            });
    }

    public aktivHighlight(key: FeatureHighlightKey): FeatureHighlight | null {
        return this.aktive().get(key) ?? null;
    }

    public aktivForaelderTekster(parent: FeatureHighlightParent): string[] {
        return [...this.aktive().entries()].filter(([key]) => FEATURE_HIGHLIGHT_PARENT[key] === parent).map(([, highlight]) => highlight.tekst);
    }

    public erForaelderAktiv(parent: FeatureHighlightParent): boolean {
        return this.aktivForaelderTekster(parent).length > 0;
    }

    public dismiss(key: FeatureHighlightKey): void {
        const highlight = this.aktivHighlight(key);
        if (!highlight) {
            return;
        }
        const next = new Set(this.dismissed());
        next.add(dismissKey(highlight));
        this.gemDismissed(next);
    }

    /** Fjerner dismiss-id'er for highlights der er slettet i Umbraco eller udløbet. */
    private ryddOpDismissed(gyldige: readonly FeatureHighlight[]): void {
        const kendte = new Set(gyldige.map(dismissKey));
        const current = this.dismissed();
        const next = new Set([...current].filter((id) => kendte.has(id)));
        if (next.size !== current.size) {
            this.gemDismissed(next);
        }
    }

    private gemDismissed(next: ReadonlySet<string>): void {
        if (next.size === 0) {
            this.localStorage.removeItem(FEATURE_HIGHLIGHT_STORAGE_KEY);
        } else {
            this.localStorage.setItem(FEATURE_HIGHLIGHT_STORAGE_KEY, JSON.stringify([...next]));
        }
        this.dismissed.set(next);
    }

    private readDismissed(): ReadonlySet<string> {
        const raw = this.localStorage.getItem(FEATURE_HIGHLIGHT_STORAGE_KEY);
        if (!raw) {
            return new Set();
        }
        try {
            const parsed: unknown = JSON.parse(raw);
            return Array.isArray(parsed) ? new Set(parsed.filter((id): id is string => typeof id === "string" && id.length > 0)) : new Set();
        } catch {
            return new Set();
        }
    }
}

function dismissKey(highlight: FeatureHighlight): string {
    return `${highlight.id}:${highlight.noegle}`;
}
